import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Link, Typography } from "@mui/material";
import WorksuiteLoginBrandLogo from "../../Worksuite/branding/WorksuiteLoginBrandLogo";
import WorksuiteArrivalExperienceShell from "../../Worksuite/arrival/WorksuiteArrivalExperienceShell";
import { buildCampaignsAccountsUrl } from "../../Worksuite/auth/identityClient";
import { readIntendedRoute } from "../../Worksuite/auth/intendedRoute";
import { AUTH_GATEWAY_VISUAL_TOKENS as TOKENS } from "../../Worksuite/auth/authGatewayVisualTokens";

export default function IdentityArrival({ mode = "login" }) {
  const [redirecting, setRedirecting] = useState(false);
  const isSignup = mode === "signup";
  const intendedPath = useMemo(() => readIntendedRoute("/listOrganization"), []);
  const target = useMemo(
    () => buildCampaignsAccountsUrl(isSignup ? "/register" : "/login", { intendedPath }),
    [isSignup, intendedPath]
  );

  useEffect(() => {
    const preload = document.createElement("link");
    preload.rel = "prefetch";
    preload.href = target;
    document.head.appendChild(preload);
    return () => preload.remove();
  }, [target]);

  const continueSecurely = () => {
    setRedirecting(true);
    window.location.assign(target);
  };

  return (
    <WorksuiteArrivalExperienceShell
      scene="login"
      panelMaxWidth={TOKENS.panel.width}
      arrivalBrandHref="https://worksuite.clikkle.com"
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        width="100%"
        minHeight={TOKENS.panel.minHeight}
        px={TOKENS.panel.paddingX}
        py={TOKENS.panel.paddingY}
        sx={{ color: "#f7f9fc" }}
      >
        <Box mb={2} sx={{ transform: "translateY(-4px)" }}>
          <Link href="https://worksuite.clikkle.com" aria-label="Worksuite home">
            <WorksuiteLoginBrandLogo
              alt="Worksuite"
              sx={{ width: TOKENS.logo.width, height: TOKENS.logo.height, objectFit: "contain" }}
            />
          </Link>
        </Box>

        <Typography component="h1" fontWeight={700} textAlign="center" sx={{ mb: 1.5, fontSize: 27, lineHeight: 1.15, letterSpacing: "-.025em" }}>
          {isSignup ? "Create your Campaigns account" : "Continue to Campaigns"}
        </Typography>

        <Typography textAlign="center" sx={{ mb: 3.5, color: "rgba(241,245,249,.72)", maxWidth: 340, lineHeight: 1.5, fontSize: 13 }}>
          {isSignup
            ? "Create your identity through Clikkle Identity, then return securely to Campaigns."
            : "Sign in through Clikkle Identity, then return securely to your Campaigns workspace."}
        </Typography>

        <Button
          fullWidth
          onClick={continueSecurely}
          disabled={redirecting}
          sx={{
            minHeight: TOKENS.button.minHeight,
            borderRadius: TOKENS.button.radius,
            color: "#fff",
            textTransform: "none",
            fontWeight: 700,
            border: TOKENS.button.border,
            background: TOKENS.button.background,
            backdropFilter: "blur(14px)",
            boxShadow: "inset 0 1px rgba(255,255,255,.10)",
            "&:hover": { background: TOKENS.button.hover },
          }}
        >
          {redirecting ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : (isSignup ? "Create account securely" : "Continue securely")}
        </Button>

        <Typography sx={{ mt: 2.2, color: "rgba(244,247,255,.68)", fontSize: 13 }}>
          {isSignup ? "Already have a Clikkle ID? " : "New to Clikkle? "}
          <Link href={isSignup ? "/login" : "/signup"} underline="hover" sx={{ fontWeight: 700, color: "#fff" }}>
            {isSignup ? "Sign in" : "Create account"}
          </Link>
        </Typography>

        <Typography variant="caption" textAlign="center" sx={{ mt: 2.5, color: "rgba(244,247,255,.5)" }}>
          Protected by Clikkle Identity · Protocol v2
        </Typography>
      </Box>
    </WorksuiteArrivalExperienceShell>
  );
}
